import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Check as CheckIcon,
  Psychology as UnderstandingIcon,
  Search as SearchIcon,
  Analytics as AnalyticsIcon,
  AutoAwesome as GenerateIcon,
} from '@mui/icons-material';

interface ProgressStage {
  id: string;
  label: string;
  icon: React.ReactNode;
  duration: number; // milliseconds before moving to next stage
}

const STAGES: ProgressStage[] = [
  {
    id: 'understanding',
    label: 'Understanding your query',
    icon: <UnderstandingIcon fontSize="small" />,
    duration: 800,
  },
  {
    id: 'searching',
    label: 'Searching documents',
    icon: <SearchIcon fontSize="small" />,
    duration: 1500,
  },
  {
    id: 'analyzing',
    label: 'Analyzing content',
    icon: <AnalyticsIcon fontSize="small" />,
    duration: 2000,
  },
  {
    id: 'generating',
    label: 'Generating response',
    icon: <GenerateIcon fontSize="small" />,
    duration: 0, // stays here until response arrives
  },
];

interface QueryProgressIndicatorProps {
  isLoading: boolean;
}

export const QueryProgressIndicator: React.FC<QueryProgressIndicatorProps> = ({
  isLoading,
}) => {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [completedStages, setCompletedStages] = useState<Set<number>>(new Set());

  // Debug log
  console.log('QueryProgressIndicator render - isLoading:', isLoading);

  // Reset when loading starts
  useEffect(() => {
    if (isLoading) {
      console.log('QueryProgressIndicator - Loading started, resetting stages');
      setCurrentStageIndex(0);
      setCompletedStages(new Set());
    }
  }, [isLoading]);

  // Progress through stages automatically
  useEffect(() => {
    if (!isLoading) return;

    const stage = STAGES[currentStageIndex];
    if (!stage || stage.duration === 0) return;

    console.log('QueryProgressIndicator - Current stage:', stage.label);

    const timer = setTimeout(() => {
      setCompletedStages((prev) => new Set([...prev, currentStageIndex]));
      if (currentStageIndex < STAGES.length - 1) {
        setCurrentStageIndex((prev) => prev + 1);
      }
    }, stage.duration);

    return () => clearTimeout(timer);
  }, [isLoading, currentStageIndex]);

  if (!isLoading) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          maxWidth: '80%',
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'secondary.main',
            m: 1,
          }}
        >
          <BotIcon />
        </Avatar>

        <Paper
          elevation={1}
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            minWidth: 280,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontWeight: 500 }}
          >
            Processing your question...
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {STAGES.map((stage, index) => {
              const isCompleted = completedStages.has(index);
              const isCurrent = index === currentStageIndex && !isCompleted;
              const isPending = index > currentStageIndex;

              return (
                <Box
                  key={stage.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    opacity: isPending ? 0.4 : 1,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  {/* Status indicator */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isCompleted
                        ? 'success.main'
                        : isCurrent
                        ? 'primary.main'
                        : 'grey.300',
                      color: isCompleted || isCurrent ? 'white' : 'grey.600',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {isCompleted ? (
                      <CheckIcon sx={{ fontSize: 18 }} />
                    ) : isCurrent ? (
                      <CircularProgress
                        size={16}
                        thickness={4}
                        sx={{ color: 'white' }}
                      />
                    ) : (
                      stage.icon
                    )}
                  </Box>

                  {/* Stage label */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: isCompleted
                        ? 'success.main'
                        : isCurrent
                        ? 'text.primary'
                        : 'text.disabled',
                      fontWeight: isCurrent ? 500 : 400,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {stage.label}
                    {isCompleted && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: 'success.main' }}
                      >
                        Done
                      </Typography>
                    )}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
